import React from 'react';

import { devWarning, OmitStrict } from '@tanium/coreui-utils';
import styled from '@tanium/react-emotion-9';
import { css } from '@tanium/style-system';

import {
  Border,
  BorderDisabled,
  BorderFocus,
  BorderInvalid,
  Fill,
  FillDisabled,
  PlaceholderText,
  ShadowFocus,
  Text,
  TextDisabled,
} from './themeDefinitions';

const StyledTextInput = styled('input', {
  shouldForwardProp: (p) => !['invalid', 'innerRef'].includes(p),
})<Pick<Props, 'invalid'>>(({ invalid }) =>
  css({
    bg: Fill,
    border: 1,
    borderColor: invalid ? BorderInvalid : Border,
    borderRadius: 1,
    color: Text,
    height: 4,
    lineHeight: '16px',
    p: 1,
    fontSize: 14,
    '::placeholder': {
      color: PlaceholderText,
    },
    ':disabled': {
      bg: FillDisabled,
      borderColor: BorderDisabled,
      color: TextDisabled,
    },
    ':focus': {
      borderColor: BorderFocus,
      boxShadow: ShadowFocus,
      // use transparent color instead of `outline: none` for Windows high contrast
      // see: https://stackoverflow.com/a/52616313
      outlineColor: 'transparent',
      // not setting outline style causes outline to appear white in Chrome
      outlineStyle: 'inherit',
    },
  }),
);

export const textInputTypes = [
  'date',
  'datetime-local',
  'email',
  'month',
  'number',
  'password',
  'search',
  'tel',
  'text',
  'time',
  'url',
  'week',
] as const;

export type TextInputType = typeof textInputTypes[number];

const limitedSupportTypes: TextInputType[] = ['date', 'datetime-local', 'month', 'time', 'week'];

export interface Props
  extends Omit<OmitStrict<JSX.IntrinsicElements['input'], 'type' | 'ref'>, 'css'> {
  type?: TextInputType;
  /**
   * Whether the input should be displayed in an invalid (error) state.
   */
  invalid?: boolean;
}

const TextInput = React.forwardRef<HTMLInputElement, Props>(({ type = 'text', ...props }, ref) => {
  devWarning(
    textInputTypes.indexOf(type) >= 0,
    `TextInput type "${type}" is unsupported and may not render as expected across browsers. Supported types are: ${textInputTypes.join(
      ', ',
    )}. You should use a supported type or a more purpose-built component instead.`,
  );

  devWarning(
    limitedSupportTypes.indexOf(type) === -1,
    `TextInput type "${type}" is not natively supported in all browsers and may render as a plain text input. Consider using a more purpose-built component instead.`,
  );

  return <StyledTextInput {...props} innerRef={ref} type={type} />;
});

TextInput.displayName = 'TextInput';

export default TextInput;
